import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import authFetch from "../../authFetch";
import mdToHtml from "../../utils/mdToHtml";

const MAX_CHARS = 1500;
const HISTORY_CAP = 20;

function QIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10"/>
      <path d="M9.1 9a3 3 0 015.8 1c0 2-3 3-3 3"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

export default function HelpBubble({ currentView, enabledModules, locale }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const isAr = locale === "ar";

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [open, messages, sending]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("help:open", onOpen);
    return () => window.removeEventListener("help:open", onOpen);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (text.length > MAX_CHARS) {
      setError(isAr ? "الرسالة طويلة جدًا" : "Message too long");
      return;
    }
    setError("");
    const nextMessages = [...messages, { role: "user", content: text }].slice(-HISTORY_CAP);
    setMessages(nextMessages);
    setInput("");
    setSending(true);
    try {
      const r = await authFetch("/api/help-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          messages: nextMessages,
          currentView: currentView || "",
          enabledModules: enabledModules || [],
          locale: isAr ? "ar" : "en",
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.error || (isAr ? "حدث خطأ" : "Something went wrong"));
      } else {
        setMessages((prev) => [...prev, { role: "assistant", content: data.text || "" }]);
      }
    } catch {
      setError(isAr ? "تعذر الاتصال" : "Connection error");
    } finally {
      setSending(false);
    }
  }, [input, sending, messages, currentView, enabledModules, isAr]);

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const greeting = isAr
    ? "مرحبًا! اسألني كيف تتنقل في التطبيق أو أين تجد ميزة معينة."
    : "Hi! Ask me how to navigate the app or where to find a feature.";

  return (
    <>
      <button
        className="help-bubble"
        onClick={() => setOpen(true)}
        aria-label={isAr ? "مساعدة" : "Help"}
        dir={isAr ? "rtl" : "ltr"}
      >
        <QIcon />
      </button>

      {open && createPortal(
        <div className="help-overlay" dir={isAr ? "rtl" : "ltr"} role="dialog" aria-modal="true" aria-label={isAr ? "مساعدة" : "Help"}>
          <div className="help-backdrop" onClick={() => setOpen(false)} />
          <div className="help-panel">
            <div className="help-head">
              <div className="help-title">
                <span className="help-dot" />
                {isAr ? "المساعد" : "Help"}
              </div>
              <button className="help-close" onClick={() => setOpen(false)} aria-label={isAr ? "إغلاق" : "Close"}>
                <CloseIcon />
              </button>
            </div>

            <div className="help-body" ref={scrollRef}>
              {messages.length === 0 && (
                <div className="help-greeting">{greeting}</div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`help-msg ${m.role}`}>
                  {m.role === "assistant"
                    ? <div className="help-bubble-text" dangerouslySetInnerHTML={{__html: mdToHtml(m.content)}}/>
                    : <div className="help-bubble-text">{m.content}</div>}
                </div>
              ))}
              {sending && (
                <div className="help-msg assistant">
                  <div className="help-bubble-text help-typing">
                    <span/><span/><span/>
                  </div>
                </div>
              )}
              {error && <div className="help-error">{error}</div>}
            </div>

            <div className="help-input-row">
              <textarea
                ref={inputRef}
                className="help-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, MAX_CHARS))}
                onKeyDown={onKeyDown}
                placeholder={isAr ? "اسأل عن التنقل..." : "Ask how to navigate..."}
                disabled={sending}
              />
              <button
                className="help-send"
                onClick={send}
                disabled={sending || !input.trim()}
                aria-label={isAr ? "إرسال" : "Send"}
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
